import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosConfig';
import './AnnouncementAdmin.css';

// Post a full-screen popup to every logged-in user at once (live socket + web
// push). Meant for maintenance windows, outages and "read this now" notices.
// The popup cannot be closed by the recipient until the duration set here runs
// out. system_admin only — this whole tab is behind the AdminDashboard.
const AnnouncementAdmin = () => {
    const [message, setMessage] = useState('');
    const [amount, setAmount] = useState(30);
    const [unit, setUnit] = useState('seconds'); // 'seconds' | 'minutes'
    const [allowResponses, setAllowResponses] = useState(true);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    const durationSeconds = unit === 'minutes' ? amount * 60 : amount;

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await axiosInstance.get('/api/announcements');
            if (data?.success) setHistory(data.data || []);
        } catch (err) {
            console.error('Error loading announcements:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const send = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;
        if (durationSeconds < 5 || durationSeconds > 3600) {
            alert('Duration must be between 5 seconds and 60 minutes.');
            return;
        }
        if (!window.confirm(
            `Show this to EVERYONE now for ${durationSeconds}s? They cannot close it until the timer ends.`
        )) return;

        try {
            setSending(true);
            await axiosInstance.post('/api/announcements', { message: message.trim(), durationSeconds, allowResponses });
            setMessage('');
            await load();
            // Open the floating replies dock so the admin can watch responses come in.
            if (allowResponses) window.dispatchEvent(new CustomEvent('announcement:open-dock'));
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to send announcement');
        } finally {
            setSending(false);
        }
    };

    const clearNow = async (id) => {
        if (!window.confirm('Pull this announcement from every screen now?')) return;
        try {
            await axiosInstance.delete(`/api/announcements/${id}`);
            await load();
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to clear announcement');
        }
    };

    const isLive = (a) => a.active && new Date(a.expiresAt).getTime() > Date.now();

    return (
        <div className="ann-admin">
            <form className="ann-admin__form" onSubmit={send}>
                <h2>Post an announcement</h2>
                <p className="ann-admin__hint">
                    Shows as a full-screen popup for every logged-in user, on every open tab, immediately
                    — plus a phone push for anyone with the app closed. The popup <strong>cannot be closed</strong>
                    {' '}until the duration below runs out. Use it for maintenance, downtime or urgent notices.
                </p>

                <label className="ann-admin__label">Message</label>
                <textarea
                    className="ann-admin__textarea"
                    rows={3}
                    maxLength={1000}
                    placeholder="e.g. The system will be down for maintenance from 9:00 to 9:30 PM."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                />

                <label className="ann-admin__label">Show for</label>
                <div className="ann-admin__duration">
                    <input
                        type="number"
                        className="ann-admin__number"
                        min={1}
                        value={amount}
                        onChange={(e) => setAmount(Math.max(1, Number(e.target.value) || 0))}
                        required
                    />
                    <select className="ann-admin__unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
                        <option value="seconds">seconds</option>
                        <option value="minutes">minutes</option>
                    </select>
                    <span className="ann-admin__resolved">= {durationSeconds}s</span>
                </div>

                <label className="ann-admin__checkbox">
                    <input
                        type="checkbox"
                        checked={allowResponses}
                        onChange={(e) => setAllowResponses(e.target.checked)}
                    />
                    <span>Let users reply — replies arrive live in the floating panel, no popup on your side</span>
                </label>

                <button type="submit" className="ann-admin__send" disabled={sending || !message.trim()}>
                    {sending ? 'Sending…' : 'Send to everyone now'}
                </button>
            </form>

            <div className="ann-admin__history">
                <h3>Recent</h3>
                {loading ? (
                    <p className="ann-admin__muted">Loading…</p>
                ) : history.length === 0 ? (
                    <p className="ann-admin__muted">No announcements yet.</p>
                ) : (
                    <ul className="ann-admin__list">
                        {history.map((a) => (
                            <li key={a.id} className={`ann-admin__item ${isLive(a) ? 'is-live' : ''}`}>
                                <div className="ann-admin__item-main">
                                    <div className="ann-admin__item-msg">{a.message}</div>
                                    <div className="ann-admin__item-meta">
                                        {new Date(a.createdAt).toLocaleString()} · {a.durationSeconds}s
                                        {a.createdByName ? ` · ${a.createdByName}` : ''}
                                        {isLive(a) && <span className="ann-admin__live-badge">LIVE</span>}
                                        {a.responseCount > 0 && (
                                            <button
                                                type="button"
                                                className="ann-admin__replies"
                                                onClick={() => window.dispatchEvent(
                                                    new CustomEvent('announcement:open-dock', { detail: { id: a.id } })
                                                )}
                                            >
                                                💬 {a.responseCount} {a.responseCount === 1 ? 'reply' : 'replies'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {isLive(a) && (
                                    <button type="button" className="ann-admin__clear" onClick={() => clearNow(a.id)}>
                                        Stop showing
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AnnouncementAdmin;
